import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { NodeToolbar, Position, useViewport } from '@xyflow/react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { getEstimatedCredits } from '@shared/billing/costEstimator';
import { useLastUsedModelStore } from '../../../../stores/lastUsedModelStore';
import {
  type ModelDisplayEntry,
  type CapabilityGroup,
  IMAGE_GROUP_ORDER,
  VIDEO_GROUP_ORDER,
  TEXT_GROUP_ORDER,
  GROUP_LABELS,
  getModelDisplay,
  buildSearchText,
  getCreditTier,
  CREDIT_TIER_STYLES,
} from './modelDisplayConfig';
// SimpleTooltip no longer needed — info panel is rendered inline

// ─── Types ────────────────────────────────────────────────────────────────────

interface AnyModelDef {
  id: string;
  name: string;
  supportsReferenceImages?: boolean;
  maxReferenceImages?: number;
  requiresReferenceImage?: boolean;
}

export interface SmartModelSelectorProps {
  mediaType: 'image' | 'video' | 'text';
  selectedModelId: string;
  onSelect: (modelId: string) => void;
  connectedRefImageCount?: number;
  models: AnyModelDef[];
  compact?: boolean;
}

// ─── Enriched model ───────────────────────────────────────────────────────────

interface EnrichedModel {
  id: string;
  apiName: string;
  display: ModelDisplayEntry;
  searchText: string;
  credits: number;
  isText: boolean;
  state: 'available' | 'warning' | 'greyed-out';
  stateMessage?: string;
  raw: AnyModelDef;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDefaultCredits(modelId: string, mediaType: 'image' | 'video' | 'text'): number {
  return getEstimatedCredits({ modelId, mediaType });
}

function computeModelState(
  model: AnyModelDef,
  connectedRefCount: number | undefined,
): { state: 'available' | 'warning' | 'greyed-out'; message?: string } {
  if (connectedRefCount === undefined) return { state: 'available' };

  const { supportsReferenceImages, requiresReferenceImage, maxReferenceImages } = model;

  if (requiresReferenceImage && connectedRefCount === 0) {
    return { state: 'greyed-out', message: 'Connect a reference image to use this model' };
  }

  if (requiresReferenceImage && maxReferenceImages && maxReferenceImages >= 2 && connectedRefCount > 0 && connectedRefCount < 2) {
    const minRequired = model.id.includes('multi') ? 2 : (model.id.includes('flf') || model.id.includes('first') ? 2 : 1);
    if (connectedRefCount < minRequired) {
      return { state: 'greyed-out', message: `Requires ${minRequired}–${maxReferenceImages} reference images (${connectedRefCount} connected)` };
    }
  }

  if (supportsReferenceImages && maxReferenceImages && connectedRefCount > maxReferenceImages) {
    return { state: 'greyed-out', message: `Supports max ${maxReferenceImages} ref image${maxReferenceImages > 1 ? 's' : ''} (${connectedRefCount} connected)` };
  }

  if (!supportsReferenceImages && connectedRefCount > 0) {
    return { state: 'warning', message: 'Reference images won\'t be used with this model' };
  }

  return { state: 'available' };
}

/** Flatten grouped + recommended + incompatible into a single selectable ID list for keyboard nav */
function buildFlatList(
  recommended: EnrichedModel[],
  groups: { models: EnrichedModel[] }[],
  incompatible: EnrichedModel[],
  hasQuery: boolean,
): string[] {
  const ids: string[] = [];
  if (!hasQuery) {
    for (const m of recommended) ids.push(m.id);
  }
  for (const g of groups) {
    for (const m of g.models) {
      if (!ids.includes(m.id)) ids.push(m.id);
    }
  }
  // incompatible models are not keyboard-navigable (greyed out)
  return ids;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SmartModelSelector({
  mediaType,
  selectedModelId,
  onSelect,
  connectedRefImageCount,
  models,
  compact = false,
}: SmartModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastUsed = useLastUsedModelStore((s) => s[mediaType]);

  // ── Zoom-adaptive scale — toolbar shrinks at low zoom, never grows past 1x ──
  const { zoom } = useViewport();
  const toolbarScale = Math.max(0.7, zoom);

  // ── Enrich models ───────────────────────────────────────────────────────
  const enrichedModels = useMemo(() => {
    return models.map((m): EnrichedModel => {
      const display = getModelDisplay(m.id, mediaType, m.name);
      const isText = mediaType === 'text';
      const credits = isText ? 0 : getDefaultCredits(m.id, mediaType);
      const { state, message } = computeModelState(m, connectedRefImageCount);
      return { id: m.id, apiName: m.name, display, searchText: buildSearchText(display), credits, isText, state, stateMessage: message, raw: m };
    });
  }, [models, mediaType, connectedRefImageCount]);

  // ── Search filter ───────────────────────────────────────────────────────
  const query = search.trim().toLowerCase();
  const filteredModels = useMemo(() => {
    if (!query) return enrichedModels;
    return enrichedModels.filter((m) => m.searchText.includes(query));
  }, [enrichedModels, query]);

  // ── Grouping ────────────────────────────────────────────────────────────
  const groupOrder = mediaType === 'video'
    ? (VIDEO_GROUP_ORDER as readonly CapabilityGroup[])
    : mediaType === 'text'
      ? (TEXT_GROUP_ORDER as readonly CapabilityGroup[])
      : (IMAGE_GROUP_ORDER as readonly CapabilityGroup[]);

  const { recommended, groups, incompatible } = useMemo(() => {
    const rec: EnrichedModel[] = [];
    if (!query) {
      if (lastUsed) {
        const lastModel = enrichedModels.find((m) => m.id === lastUsed.modelId);
        if (lastModel && lastModel.state !== 'greyed-out') rec.push(lastModel);
      }
      const compatible = enrichedModels
        .filter((m) => m.state === 'available' && !rec.some((r) => r.id === m.id))
        .sort((a, b) => a.credits - b.credits);
      for (const m of compatible) {
        if (rec.length >= 4) break;
        rec.push(m);
      }
    }

    // IDs already shown in Recommended — exclude from capability groups to avoid duplicates
    const recIds = new Set(rec.map((m) => m.id));

    const available = filteredModels.filter((m) => m.state !== 'greyed-out');
    const greyedOut = filteredModels.filter((m) => m.state === 'greyed-out');
    const grouped: { group: CapabilityGroup; label: string; emoji: string; models: EnrichedModel[] }[] = [];
    for (const g of groupOrder) {
      const gModels = available
        .filter((m) => m.display.group === g && !recIds.has(m.id))
        .sort((a, b) => a.credits - b.credits);
      if (gModels.length > 0) {
        const info = GROUP_LABELS[g];
        grouped.push({ group: g, label: info.label, emoji: info.emoji, models: gModels });
      }
    }
    return { recommended: rec, groups: grouped, incompatible: greyedOut };
  }, [enrichedModels, filteredModels, query, groupOrder, lastUsed]);

  // ── Flat list for keyboard nav ──────────────────────────────────────────
  const flatIds = useMemo(
    () => buildFlatList(recommended, groups, incompatible, !!query),
    [recommended, groups, incompatible, query],
  );

  // ── Selected display ────────────────────────────────────────────────────
  const selectedEnriched = useMemo(
    () => enrichedModels.find((m) => m.id === selectedModelId) || enrichedModels[0],
    [enrichedModels, selectedModelId],
  );

  // ── Open / Close ────────────────────────────────────────────────────────
  const open = useCallback(() => {
    setIsOpen(true);
    setSearch('');
    setHighlightedId(null);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    setHighlightedId(null);
  }, []);

  const handleSelect = useCallback((modelId: string) => {
    onSelect(modelId);
    close();
  }, [onSelect, close]);

  // ── Focus search on open ────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [isOpen]);

  // ── Click-outside dismiss ───────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };
    // Use capture phase so we beat ReactFlow's event handling
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [isOpen, close]);

  // ── Escape key ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isOpen, close]);

  // ── Keyboard navigation ─────────────────────────────────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (flatIds.length === 0) return;

      const currentIdx = highlightedId ? flatIds.indexOf(highlightedId) : -1;
      let nextIdx: number;

      if (e.key === 'ArrowDown') {
        nextIdx = currentIdx < flatIds.length - 1 ? currentIdx + 1 : 0;
      } else {
        nextIdx = currentIdx > 0 ? currentIdx - 1 : flatIds.length - 1;
      }

      const nextId = flatIds[nextIdx];
      setHighlightedId(nextId);

      // Scroll into view
      requestAnimationFrame(() => {
        const el = rowRefs.current.get(nextId);
        el?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      });
    }

    if (e.key === 'Enter' && highlightedId) {
      e.preventDefault();
      const model = enrichedModels.find((m) => m.id === highlightedId);
      if (model && model.state !== 'greyed-out') {
        handleSelect(highlightedId);
      }
    }

    if (e.key === 'Home') {
      e.preventDefault();
      if (flatIds.length > 0) {
        setHighlightedId(flatIds[0]);
        requestAnimationFrame(() => {
          listRef.current?.scrollTo({ top: 0, behavior: 'auto' });
        });
      }
    }

    if (e.key === 'End') {
      e.preventDefault();
      if (flatIds.length > 0) {
        const lastId = flatIds[flatIds.length - 1];
        setHighlightedId(lastId);
        requestAnimationFrame(() => {
          const el = rowRefs.current.get(lastId);
          el?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        });
      }
    }
  }, [isOpen, flatIds, highlightedId, enrichedModels, handleSelect]);

  // Reset highlight when search changes
  useEffect(() => {
    if (flatIds.length > 0) {
      setHighlightedId(flatIds[0]);
    } else {
      setHighlightedId(null);
    }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Register row ref ────────────────────────────────────────────────────
  const setRowRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);

  // ── Sub-components ──────────────────────────────────────────────────────

  const CreditBadge = useCallback(({ credits, isText, size = 'sm' }: { credits: number; isText: boolean; size?: 'sm' | 'xs' }) => {
    const tier = getCreditTier(credits, isText);
    const s = CREDIT_TIER_STYLES[tier];
    const cls = size === 'xs'
      ? `${s.bg} ${s.text} text-xs font-semibold px-1 py-px rounded whitespace-nowrap leading-tight`
      : `${s.bg} ${s.text} text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap`;
    return <span className={cls}>{isText ? 'Free' : `${credits.toLocaleString()} cr`}</span>;
  }, []);

  function InfoPanel({ model }: { model: EnrichedModel }) {
    const { display } = model;
    const capItems = display.tooltip.capabilities
      ? display.tooltip.capabilities.split(',').map((s) => s.trim().replace(/^[✅⚙️🔊]\s*/, ''))
      : [];
    const priceItems = display.tooltip.pricing
      ? display.tooltip.pricing.split('/').map((s) => s.trim())
      : [];

    return (
      <div className="w-72">
        {/* Header */}
        <div className="px-4 py-2.5 bg-gray-800">
          <div className="text-base font-semibold text-white leading-tight">{display.displayName}</div>
          <div className="text-xs text-gray-400 leading-tight mt-1">{display.technicalName}</div>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3">
          <p className="text-base text-gray-600 leading-relaxed">{display.tooltip.description}</p>

          {capItems.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Capabilities</div>
              <ul className="space-y-0.5">
                {capItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-base text-gray-600 leading-relaxed">
                    <span className="text-secondary-400 mt-[5px] flex-shrink-0 text-[8px]">●</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {priceItems.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Pricing</div>
              {priceItems.length === 1 ? (
                <div className="text-base text-gray-600 leading-relaxed">{priceItems[0]}</div>
              ) : (
                <ul className="space-y-0.5">
                  {priceItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-base text-gray-600 leading-relaxed">
                      <span className="text-secondary-400 mt-[5px] flex-shrink-0 text-[8px]">●</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer — Best for */}
        {display.tooltip.bestFor && (
          <div className="px-4 py-2.5 bg-secondary-50 border-t border-secondary-100">
            <div className="text-xs font-semibold text-secondary-600 uppercase tracking-wide leading-none">Best for</div>
            <div className="text-base text-secondary-700 leading-relaxed mt-1">{display.tooltip.bestFor}</div>
          </div>
        )}
      </div>
    );
  }

  function ModelRow({ model, isLastUsed, isHighlighted }: { model: EnrichedModel; isLastUsed?: boolean; isHighlighted?: boolean }) {
    const isSelected = model.id === selectedModelId;
    const isGreyedOut = model.state === 'greyed-out';
    const isWarning = model.state === 'warning';

    return (
      <div
        ref={(el) => setRowRef(model.id, el)}
        className={[
          'flex items-center gap-2 mx-1 px-2 py-[5px] rounded transition-colors cursor-pointer',
          isGreyedOut && 'opacity-35 cursor-not-allowed',
          !isGreyedOut && !isSelected && !isHighlighted && 'hover:bg-gray-50',
          isHighlighted && !isGreyedOut && 'bg-gray-100',
          isSelected && !isHighlighted && !highlightedId && 'bg-blue-50/60',
        ].filter(Boolean).join(' ')}
        onClick={() => { if (!isGreyedOut) handleSelect(model.id); }}
        onMouseEnter={() => { if (!isGreyedOut) setHighlightedId(model.id); }}
      >
        {/* Left: name + tags */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={`text-base font-medium truncate ${isSelected ? 'text-blue-700' : isGreyedOut ? 'text-gray-400' : 'text-gray-800'}`}>
              {model.display.displayName}
            </span>
            {isLastUsed && (
              <span className="text-xs font-medium px-1 py-px rounded bg-violet-50 text-violet-500 uppercase tracking-wide whitespace-nowrap">
                Recent
              </span>
            )}
          </div>
          <div className="text-xs leading-tight mt-px truncate">
            {isGreyedOut && model.stateMessage
              ? <span className="text-gray-400 italic">{model.stateMessage}</span>
              : isWarning && model.stateMessage
                ? <span className="text-warning-500">{model.stateMessage}</span>
                : <span className="text-gray-400">{model.display.subtitleTags.join(' · ')}</span>}
          </div>
        </div>

        {/* Right: credits + checkmark */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <CreditBadge credits={model.credits} isText={model.isText} size="xs" />
          {isSelected && (
            <Check className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
          )}
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────

  const triggerText = selectedEnriched?.display.displayName ?? 'Select model';
  const triggerCredits = selectedEnriched?.credits ?? 0;

  return (
    <>
      {/* Trigger — styled to match native selects in the app */}
      <button
        ref={triggerRef}
        type="button"
        onClick={isOpen ? close : open}
        className={[
          'w-full flex items-center justify-between gap-1.5 rounded-md border bg-white text-left transition-all',
          compact ? 'text-xs px-2 py-1' : 'text-xs px-2 py-1',
          isOpen
            ? 'border-primary-300 ring-1 ring-primary-100'
            : 'border-gray-200 hover:border-gray-300',
        ].join(' ')}
      >
        <span className="truncate text-gray-700">{triggerText}</span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <CreditBadge credits={triggerCredits} isText={mediaType === 'text'} size="xs" />
          <ChevronDown
            className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Side panel via NodeToolbar — renders outside the node, above all content */}
      <NodeToolbar isVisible={isOpen} position={Position.Right} offset={10} align="start">
        <div
          ref={popoverRef}
          className="nodrag nopan nowheel flex items-start gap-2"
          onKeyDown={handleKeyDown}
          style={{ transform: `scale(${toolbarScale})`, transformOrigin: 'top left' }}
        >
          {/* Selector panel */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col overflow-hidden animate-in fade-in-0 zoom-in-[0.98] duration-150 flex-shrink-0" style={{ width: 340 }}>
            {/* Search */}
            <div className="px-2 pt-2 pb-1.5">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search models…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-7 pr-7 py-1.5 text-base rounded-md border border-gray-200 bg-gray-50 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                />
                {search && (
                  <button
                    onClick={() => { setSearch(''); searchInputRef.current?.focus(); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Model list */}
            <div ref={listRef} className="overflow-y-auto max-h-[360px] pb-1">
              {/* Recommended */}
              {!query && recommended.length > 0 && (
                <div className="mb-0.5">
                  <div className="px-3 pt-2 pb-1 text-xs font-bold text-gray-400 uppercase tracking-widest select-none">
                    ⭐ Recommended
                  </div>
                  {recommended.map((m) => (
                    <ModelRow
                      key={`rec-${m.id}`}
                      model={m}
                      isLastUsed={lastUsed?.modelId === m.id}
                      isHighlighted={highlightedId === m.id}
                    />
                  ))}
                  <div className="mx-3 mt-1 border-b border-gray-100" />
                </div>
              )}

              {/* Capability groups */}
              {groups.map((g, gi) => (
                <div key={g.group}>
                  <div className="px-3 pt-2 pb-1 text-xs font-bold text-gray-400 uppercase tracking-widest select-none">
                    {g.emoji} {g.label}
                  </div>
                  {g.models.map((m) => (
                    <ModelRow
                      key={m.id}
                      model={m}
                      isHighlighted={highlightedId === m.id}
                    />
                  ))}
                  {gi < groups.length - 1 && <div className="mx-3 mt-1 border-b border-gray-100" />}
                </div>
              ))}

              {/* Not Compatible */}
              {incompatible.length > 0 && (
                <div>
                  <div className="mx-3 mt-1.5 mb-1 border-b border-gray-100" />
                  <div className="px-3 pt-1 pb-1 text-xs font-bold text-gray-400 uppercase tracking-widest select-none">
                    Not compatible
                  </div>
                  {incompatible.map((m) => (
                    <ModelRow
                      key={`inc-${m.id}`}
                      model={m}
                      isHighlighted={highlightedId === m.id}
                    />
                  ))}
                </div>
              )}

              {/* No results */}
              {filteredModels.length === 0 && (
                <div className="px-3 py-6 text-center">
                  <div className="text-base text-gray-400">No models match "<span className="font-medium text-gray-500">{search}</span>"</div>
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-2 py-1 border-t border-gray-100 bg-gray-50/50">
              <div className="text-xs text-gray-400 flex items-center gap-2">
                <span><kbd className="px-1 py-px bg-gray-100 rounded text-xs font-mono">↑↓</kbd> navigate</span>
                <span><kbd className="px-1 py-px bg-gray-100 rounded text-xs font-mono">↵</kbd> select</span>
                <span><kbd className="px-1 py-px bg-gray-100 rounded text-xs font-mono">esc</kbd> close</span>
              </div>
            </div>
          </div>

          {/* Side info panel — shows for highlighted model */}
          {highlightedId && (() => {
            const hModel = enrichedModels.find((m) => m.id === highlightedId);
            if (!hModel) return null;
            return (
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden animate-in fade-in-0 slide-in-from-left-2 duration-150 flex-shrink-0">
                <InfoPanel model={hModel} />
              </div>
            );
          })()}
        </div>
      </NodeToolbar>
    </>
  );
}
